import { parseComponents, extractVariables, type ParsedComponent } from './template-components';

export interface TemplateProblem {
  /** Stable code, matching `services/channel-wa/Services/TemplateValidator.cs`. */
  code: string;
  /** Catalog key for the message. */
  key: string;
  /** Interpolation values for that key. */
  vars?: Record<string, string | number>;
}

const HEADER_MAX = 60;
const BODY_MAX = 1024;
const FOOTER_MAX = 60;
const BUTTON_TEXT_MAX = 25;
const BUTTONS_MAX = 10;
const URL_BUTTONS_MAX = 2;
const PHONE_BUTTONS_MAX = 1;

/** Meta accepts lower-case letters, digits and underscores in a name. */
const LEGAL_NAME = /^[a-z0-9_]+$/;
const NAME_MAX = 512;

const BARE_URL = /https?:\/\/\S+/i;
/** A link button left pointing at the suggestion's own example domain. */
const EXAMPLE_URL = /(^|\/\/|\.)example\.(com|net|org|co\.za)(\/|$|:)/i;

/**
 * Checks a template against the rules Meta will reject it for.
 *
 * `templates.md` §4 item 1 called this "the biggest single gap on the page":
 * the editor checked that the code was non-empty and that the JSON parsed, and
 * every real authoring rule was discovered by rejection, hours later, through
 * a "Note" column, after the template had entered a 24-hour review queue and
 * become immutable.
 *
 * These rules are the same ones `TemplateValidator.cs` enforces, so the server
 * cannot accept something this rejects, and this cannot block something the
 * server would accept. Kept as codes plus catalog keys, because the console is
 * translated and the service's own messages are not.
 */
export function validateTemplate(componentsJson: string, category: string): TemplateProblem[] {
  const problems: TemplateProblem[] = [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(componentsJson || '[]');
  } catch {
    return [{ code: 'COMPONENTS_INVALID_JSON', key: 'templates.validate.invalidJson' }];
  }
  if (!Array.isArray(parsed)) {
    return [{ code: 'COMPONENTS_NOT_ARRAY', key: 'templates.validate.notArray' }];
  }

  const components: ParsedComponent[] = parseComponents(componentsJson);
  const counts = new Map<string, number>();
  let body: ParsedComponent | undefined;

  for (const c of components) {
    counts.set(c.type, (counts.get(c.type) ?? 0) + 1);
    if (c.type === 'BODY') body = c;
    /*
     * Media header rules. Meta rejects all three of these, hours later, once
     * the template is already immutable, so they are worth catching in the
     * editor where a fix costs nothing.
     */
    if (c.type === 'HEADER') {
      const format = (c.format ?? 'TEXT').toUpperCase();
      const isMedia = format === 'IMAGE' || format === 'VIDEO' || format === 'DOCUMENT';

      if (isMedia && !c.example?.header_handle?.[0]) {
        problems.push({
          code: 'HEADER_NO_SAMPLE',
          key: 'templates.validate.headerNoSample',
          vars: { format }
        });
      }
      if (isMedia && (c.text?.length ?? 0) > 0) {
        problems.push({ code: 'HEADER_MEDIA_WITH_TEXT', key: 'templates.validate.headerMediaWithText' });
      }
      if (format === 'TEXT' && !(c.text ?? '').trim()) {
        problems.push({ code: 'HEADER_TEXT_EMPTY', key: 'templates.validate.headerTextEmpty' });
      }
    }

    /*
     * Buttons. The wizard offers them as a form; before it they were
     * reachable only by hand-writing Meta's schema into a textarea, so
     * nothing checked them on either side.
     */
    if (c.type === 'BUTTONS') validateButtons(c, problems);

    if (c.type === 'HEADER' && (c.text?.length ?? 0) > HEADER_MAX) {
      problems.push({ code: 'HEADER_TOO_LONG', key: 'templates.validate.headerTooLong', vars: { length: c.text!.length, max: HEADER_MAX } });
    }
    if (c.type === 'FOOTER' && (c.text?.length ?? 0) > FOOTER_MAX) {
      problems.push({ code: 'FOOTER_TOO_LONG', key: 'templates.validate.footerTooLong', vars: { length: c.text!.length, max: FOOTER_MAX } });
    }
    if (c.type === 'BODY' && (c.text?.length ?? 0) > BODY_MAX) {
      problems.push({ code: 'BODY_TOO_LONG', key: 'templates.validate.bodyTooLong', vars: { length: c.text!.length, max: BODY_MAX } });
    }
  }

  for (const single of ['HEADER', 'BODY', 'FOOTER', 'BUTTONS']) {
    if ((counts.get(single) ?? 0) > 1) {
      problems.push({ code: `${single}_REPEATED`, key: 'templates.validate.repeated', vars: { type: single } });
    }
  }

  if (!body) {
    problems.push({ code: 'BODY_MISSING', key: 'templates.validate.bodyMissing' });
  } else {
    const text = (body.text ?? '').trim();
    // Meta rejects a body that opens or closes on a placeholder: the rendered
    // message would begin or end with whatever was substituted.
    if (text.startsWith('{{')) problems.push({ code: 'BODY_STARTS_WITH_VARIABLE', key: 'templates.validate.startsWithVariable' });
    if (text.endsWith('}}')) problems.push({ code: 'BODY_ENDS_WITH_VARIABLE', key: 'templates.validate.endsWithVariable' });
    if (category.toLowerCase() === 'marketing' && BARE_URL.test(body.text ?? '')) {
      problems.push({ code: 'MARKETING_URL_IN_BODY', key: 'templates.validate.marketingUrl' });
    }
  }

  // Placeholders are positional, so they must run 1, 2, 3 … with no gaps.
  const numbers = extractVariables(components).sort((a, b) => a - b);
  if (numbers.length > 0) {
    if (numbers[0] !== 1) {
      problems.push({ code: 'VARIABLES_NOT_FROM_ONE', key: 'templates.validate.notFromOne' });
    }
    let expected = 1;
    for (const n of numbers) {
      if (n !== expected) {
        problems.push({ code: 'VARIABLES_NOT_CONTIGUOUS', key: 'templates.validate.notContiguous', vars: { missing: expected } });
        break;
      }
      expected++;
    }
  }

  return problems;
}

/** Pretty-prints a components blob, leaving it alone if it will not parse. */
export function formatComponents(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}


/**
 * Meta's button rules, the same set
 * `services/channel-wa/Services/TemplateValidator.cs` refuses on.
 */
function validateButtons(c: ParsedComponent, problems: TemplateProblem[]): void {
  const buttons = c.buttons ?? [];
  if (buttons.length === 0) {
    problems.push({ code: 'BUTTONS_EMPTY', key: 'templates.validate.buttonsEmpty' });
    return;
  }
  if (buttons.length > BUTTONS_MAX) {
    problems.push({
      code: 'BUTTONS_TOO_MANY',
      key: 'templates.validate.buttonsTooMany',
      vars: { count: buttons.length, max: BUTTONS_MAX }
    });
  }

  let urls = 0;
  let phones = 0;

  for (const b of buttons) {
    const type = (b.type ?? '').toUpperCase();
    if (!['QUICK_REPLY', 'URL', 'PHONE_NUMBER', 'COPY_CODE', 'OTP', 'FLOW'].includes(type)) {
      problems.push({ code: 'BUTTON_BAD_TYPE', key: 'templates.validate.buttonBadType', vars: { type } });
    }
    if (type !== 'COPY_CODE') {
      if (!(b.text ?? '').trim()) {
        problems.push({ code: 'BUTTON_TEXT_EMPTY', key: 'templates.validate.buttonTextEmpty' });
      } else if (b.text.length > BUTTON_TEXT_MAX) {
        problems.push({
          code: 'BUTTON_TEXT_TOO_LONG',
          key: 'templates.validate.buttonTextTooLong',
          vars: { text: b.text, length: b.text.length, max: BUTTON_TEXT_MAX }
        });
      }
    }
    if (type === 'URL') {
      urls++;
      if (!(b.url ?? '').trim()) {
        problems.push({ code: 'BUTTON_URL_MISSING', key: 'templates.validate.buttonUrlMissing' });
      }
    }
    if (type === 'PHONE_NUMBER') {
      phones++;
      if (!(b.phone_number ?? '').trim()) {
        problems.push({ code: 'BUTTON_PHONE_MISSING', key: 'templates.validate.buttonPhoneMissing' });
      }
    }
  }

  if (urls > URL_BUTTONS_MAX) {
    problems.push({ code: 'BUTTON_URL_REPEATED', key: 'templates.validate.buttonUrlRepeated', vars: { count: urls, max: URL_BUTTONS_MAX } });
  }
  if (phones > PHONE_BUTTONS_MAX) {
    problems.push({ code: 'BUTTON_PHONE_REPEATED', key: 'templates.validate.buttonPhoneRepeated', vars: { count: phones, max: PHONE_BUTTONS_MAX } });
  }
}

/**
 * What is wrong with the template's name.
 *
 * Mirrors `TemplateNames.Problems`. The service refuses these at SUBMIT rather
 * than at create, because a draft is a draft; the wizard refuses them here so
 * nobody reaches the submit button with a name Meta will certainly refuse.
 */
export function validateName(name: string): TemplateProblem[] {
  const problems: TemplateProblem[] = [];
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    problems.push({ code: 'NAME_EMPTY', key: 'templates.validate.nameEmpty' });
    return problems;
  }
  if (trimmed.length > NAME_MAX) {
    problems.push({ code: 'NAME_TOO_LONG', key: 'templates.validate.nameTooLong', vars: { length: trimmed.length, max: NAME_MAX } });
  }
  if (!LEGAL_NAME.test(trimmed)) {
    problems.push({ code: 'NAME_ILLEGAL', key: 'templates.validate.nameIllegal' });
  }
  return problems;
}

/**
 * Advice rather than refusals.
 *
 * A placeholder with no sample value is not refused by the service: it fills
 * in a plausible one ("Sample 1") before the submit, so Meta accepts the
 * template and reviews it against wording nobody chose. That is worth saying
 * loudly and worth fixing, but it is not a reason to disable the button, and
 * the same split exists in `TemplateValidator.Advisories`.
 */
export function advisoriesFor(componentsJson: string): TemplateProblem[] {
  const advisories: TemplateProblem[] = [];
  const components = parseComponents(componentsJson);

  for (const c of components) {
    /*
     * A link button still pointing at example.com. Every starter in the library
     * ships with one, because suggested wording cannot know the customer's own
     * tracking address. Meta accepts it, which is exactly why it is worth
     * saying: the template would be approved and then send every customer to a
     * domain the business does not own.
     */
    if (c.type === 'BUTTONS') {
      for (const b of c.buttons ?? []) {
        if (b.url && EXAMPLE_URL.test(b.url)) {
          advisories.push({ code: 'BUTTON_URL_PLACEHOLDER', key: 'templates.validate.buttonUrlPlaceholder' });
        }
      }
    }
    if (c.type !== 'BODY' || !c.text) continue;
    let wanted = 0;
    for (const m of c.text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > wanted) wanted = n;
    }
    if (wanted === 0) continue;

    const have = bodySampleCount(componentsJson);
    if (have < wanted) {
      advisories.push({
        code: 'BODY_EXAMPLES_MISSING',
        key: 'templates.validate.samplesMissing',
        vars: { have, wanted }
      });
    }
  }

  return advisories;
}

/** How many samples sit at `example.body_text[0]`. */
function bodySampleCount(componentsJson: string): number {
  try {
    const raw = JSON.parse(componentsJson || '[]');
    if (!Array.isArray(raw)) return 0;
    for (const c of raw) {
      if (String(c?.type ?? '').toUpperCase() !== 'BODY') continue;
      const rows = c?.example?.body_text;
      if (!Array.isArray(rows) || !Array.isArray(rows[0])) return 0;
      return (rows[0] as unknown[]).filter((v) => typeof v === 'string' && v.trim()).length;
    }
  } catch {
    return 0;
  }
  return 0;
}
